'use client';

import Button from './Button';
import { mergeClasses } from '@/helpers/style';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
} from '@heroui/dropdown';
import clsx from 'clsx';
import React, { useState } from 'react';
import { BottomSheet } from '@/components/Layouts/Dashboard/BottomSheet';
import useScreenSize from '@/hooks/useScreenSize';
import { useTranslations } from 'next-intl';

const DropdownButton: React.FC<{
  text?: string | React.ReactElement;
  buttonProps?: React.ComponentProps<typeof Button>;
  dropdownButtonProps?: React.ComponentProps<typeof Button>;
  dropdownProps?: Omit<React.ComponentProps<typeof Dropdown>, 'children'>;
  children?: React.ReactElement;
  onSelectItem?: (item: string) => void;
  loadingStates?: Record<string, boolean>;
  mobileContent?: React.ReactElement;
}> = ({
  text,
  buttonProps,
  dropdownProps,
  children,
  dropdownButtonProps,
  onSelectItem,
  loadingStates = {},
  mobileContent,
}) => {
  const t = useTranslations('components.dropzone.dropdown');
  const [open, setOpen] = useState(false);
  const screenSize = useScreenSize();
  const isMobile = screenSize['max-lg'];

  const renderDropdown = () => (
    <Dropdown
      placement="bottom-end"
      {...{
        ...dropdownProps,
        classNames: {
          ...mergeClasses([
            { content: '!rounded-extra w-[216px]' },
            dropdownProps?.classNames as Record<string, string>,
          ]),
        },
      }}
    >
      <DropdownTrigger>
        <Button
          isIconOnly
          {...{
            ...dropdownButtonProps,
            className: clsx('!rounded-s-none', dropdownButtonProps?.className),
          }}
        >
          <FontAwesomeIcon width={12} icon={['fas', 'chevron-down']} />
        </Button>
      </DropdownTrigger>
      {children || (
        <DropdownMenu
          aria-label="Action event example"
          classNames={{
            list: '[&_li]:h-10 body2 text-gray-dark [&_svg]:text-primary-blue [&_li]:!rounded-extra',
          }}
        >
          <DropdownItem
            startContent={<FontAwesomeIcon icon="folder" />}
            className="data-[hover]:bg-primary-extra-light-blue2"
            key="device"
            onClick={() => onSelectItem?.('device')}
          >
            {t('from_device')}
          </DropdownItem>
          <DropdownItem
            startContent={
              loadingStates['google-drive'] ? (
                <span className="animate-spin w-4 h-4">
                  <FontAwesomeIcon icon="spinner" />
                </span>
              ) : (
                <FontAwesomeIcon icon={['fab', 'google-drive']} />
              )
            }
            className="data-[hover]:bg-primary-extra-light-blue2"
            key="google-drive"
            onClick={() => onSelectItem?.('google-drive')}
            isReadOnly={loadingStates['google-drive']}
          >
            {t('from_google_drive')} {loadingStates['google-drive'] && '...'}
          </DropdownItem>
          <DropdownItem
            startContent={
              loadingStates['dropbox'] ? (
                <span className="animate-spin w-4 h-4">
                  <FontAwesomeIcon icon="spinner" />
                </span>
              ) : (
                <FontAwesomeIcon icon={['fab', 'dropbox']} />
              )
            }
            className="data-[hover]:bg-primary-extra-light-blue2"
            key="dropbox"
            onClick={() => onSelectItem?.('dropbox')}
            isReadOnly={loadingStates['dropbox']}
          >
            {t('from_dropbox')} {loadingStates['dropbox'] && '...'}
          </DropdownItem>
        </DropdownMenu>
      )}
    </Dropdown>
  );

  const renderMobileContent = () => {
    if (!mobileContent)
      return (
        <BottomSheet isOpen={open} onClose={() => setOpen(false)}>
          <div className="p-4">
            <div className="rounded-extra overflow-hidden">
              <button
                onClick={() => onSelectItem?.('device')}
                className={clsx(
                  'w-full h-10 px-4 text-left body2 text-gray-dark hover:bg-primary-extra-light-blue2 flex items-center gap-3'
                )}
              >
                <FontAwesomeIcon icon="folder" />
                {t('from_device')}
              </button>

              <button
                className={clsx(
                  'w-full h-10 px-4 text-left body2 text-gray-dark hover:bg-primary-extra-light-blue2 flex items-center gap-3'
                )}
                onClick={() => onSelectItem?.('google-drive')}
              >
                {loadingStates['google-drive'] ? (
                  <span className="animate-spin w-4 h-4">
                    <FontAwesomeIcon icon="spinner" />
                  </span>
                ) : (
                  <FontAwesomeIcon icon={['fab', 'google-drive']} />
                )}
                {t('from_google_drive')}{' '}
                {loadingStates['google-drive'] && '...'}
              </button>

              <button
                className={clsx(
                  'w-full h-10 px-4 text-left body2 text-gray-dark hover:bg-primary-extra-light-blue2 flex items-center gap-3'
                )}
                onClick={() => onSelectItem?.('dropbox')}
              >
                {loadingStates['dropbox'] ? (
                  <span className="animate-spin w-4 h-4">
                    <FontAwesomeIcon icon="spinner" />
                  </span>
                ) : (
                  <FontAwesomeIcon icon={['fab', 'dropbox']} />
                )}
                {t('from_dropbox')} {loadingStates['dropbox'] && '...'}
              </button>
            </div>
          </div>
        </BottomSheet>
      );

    return (
      <BottomSheet isOpen={open} onClose={() => setOpen(false)}>
        {mobileContent}
      </BottomSheet>
    );
  };

  return (
    <div className="flex items-center gap-[1px]">
      <Button
        startContent={
          buttonProps?.startContent || (
            <FontAwesomeIcon width={14} icon="file-import" />
          )
        }
        {...{
          ...buttonProps,
          className: clsx('', buttonProps?.className),
        }}
      >
        {text}
      </Button>

      {isMobile ? (
        <>
          <Button
            isIconOnly
            {...dropdownButtonProps}
            className={clsx('!rounded-s-none', dropdownButtonProps?.className)}
            onClick={() => setOpen(true)}
          >
            <FontAwesomeIcon width={12} icon={['fas', 'chevron-down']} />
          </Button>
          {renderMobileContent()}
        </>
      ) : (
        renderDropdown()
      )}
    </div>
  );
};

export default DropdownButton;
